import { inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { ContractsDataService } from '@ffd/api-client';
import { pathParameter } from '@libs/shared-services';
import { isUnsupportedPms, SITE_INSTALL_REQUIREMENTS_STATE_KEY } from '@libs/site-setup';
import { DISABLE_GLOBAL_ERROR_TOASTS, routes } from '@libs/utils';

/**
 * Showcase is the demo site every user has, so it is reachable before a real practice is installed.
 * Practices on a PMS other than Dentrix, Open Dental and Eaglesoft are not supported yet, so they belong on the
 * installation-pending screen instead of the demo.
 *
 * The request is allowed to fail open: a network error must not lock the user out of the demo site.
 *
 * The redirect carries the response, so the setup flow reuses it instead of requesting it a second time.
 */
export const ShowcasePmsGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean | RedirectCommand> | boolean => {
  if (route.paramMap.get(pathParameter('siteId')) !== routes.showcaseSiteId) {
    return true;
  }

  const router = inject(Router);

  return inject(ContractsDataService)
    .getSiteInstallRequirements(undefined, undefined, {
      context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
    })
    .pipe(
      map((siteInstallRequirements) => {
        if (isUnsupportedPms(siteInstallRequirements)) {
          return new RedirectCommand(router.createUrlTree([routes.setup, routes.waitlistBlocker]), {
            state: { [SITE_INSTALL_REQUIREMENTS_STATE_KEY]: siteInstallRequirements },
          });
        }

        return true;
      }),
      catchError(() => of(true)),
    );
};
